import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Menu, UserCircle2, X, ChevronDown, HeartHandshake, Settings } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PlantaoToggle } from "./PlantaoToggle";
import { OnboardingTour } from "./OnboardingTour";
import BackButton from "./BackButton";

// Sub-dropdown "Operação" (consultor + admin): agrupa Auditoria / Motivos /
// Escala pra desafogar a barra superior sem esconder acesso.
const OpsDropdown = ({ role, closeMobile }) => {
  const [open, setOpen] = useState(false);
  const items = [
    { to: "/consultor/auditoria", label: "Auditoria", testId: "nav-audit" },
    { to: "/consultor/motivos", label: "Motivos", testId: "nav-reasons" },
    ...(role === "admin" ? [{ to: "/admin/escala", label: "Escala", testId: "nav-schedule" }] : []),
  ];
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid="nav-ops-toggle"
        className="text-sm text-slate-500 hover:text-[#0B2B62] px-2.5 py-2 rounded-md hover:bg-slate-50 transition-colors inline-flex items-center gap-1 whitespace-nowrap">
        <Settings className="w-3.5 h-3.5" strokeWidth={1.8} />
        Operação <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40" data-testid="nav-ops-menu">
          {items.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testId}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setOpen(false); closeMobile && closeMobile(); }}
              className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#0B2B62]">
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// Links públicos que antes moravam no rodapé — agora ficam no topo.
// Group "Explorar" evita poluir o navbar em resoluções médias.
// NOTA: `/unidades` fica OCULTO exclusivamente na página /atendimento
// (landing usada em campanhas Google Ads / Meta Ads). No restante do site
// institucional o link continua visível normalmente.
const EXPLORE_LINKS_ALL = [
  { to: "/acompanhar", label: "Acompanhar caso", testId: "nav-lookup", publicVisible: true },
  { to: "/unidades", label: "Unidades parceiras", testId: "nav-clinics", publicVisible: false },
  { to: "/profissionais", label: "Profissionais credenciados", testId: "nav-professionals", publicVisible: true },
  { to: "/blog", label: "Blog", testId: "nav-blog", publicVisible: true },
  { to: "/estatisticas", label: "Estatísticas", testId: "nav-stats", publicVisible: true },
  { to: "/impacto", label: "Impacto", testId: "nav-impact", publicVisible: true },
  { to: "/ranking", label: "Ranking", testId: "nav-ranking", publicVisible: true },
];
const JOIN_LINKS = [
  { to: "/seja-consultor", label: "Ser consultor", testId: "nav-apply-consultor" },
  { to: "/seja-parceiro", label: "Ser unidade parceira", testId: "nav-apply-clinic" },
  { to: "/seja-profissional", label: "Ser profissional credenciado", testId: "nav-apply-profissional" },
];

// Encurta "Ricardo Pires de Oliveira" → "Ricardo P." para caber no navbar
// sem forçar 3 linhas. O nome completo continua no `title` (tooltip) e no
// perfil. Preserva nome único (sem sobrenome) intacto.
const shortName = (full) => {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] || "";
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  // Na landing /atendimento (usada em campanhas Google Ads/Meta), o link
  // público para o diretório de "Unidades parceiras" fica oculto do topo.
  // Objetivo: manter a landing enxuta e evitar exposição em massa da rede
  // credenciada em contexto de anúncio pago. Nas demais páginas do site
  // institucional o menu segue completo, como antes.
  const location = useLocation();
  const isAdsLanding = location.pathname === "/atendimento";
  const EXPLORE_LINKS = isAdsLanding
    ? EXPLORE_LINKS_ALL.filter((l) => l.publicVisible)
    : EXPLORE_LINKS_ALL;

  // Links públicos (visíveis sempre — antes moravam no rodapé)
  // "Preciso de ajuda" e "Faça parte" só aparecem para visitantes e famílias —
  // são CTAs voltados ao público leigo. Staff logado (admin/consultor/clínica)
  // não precisa desses itens e sua ausência é o que impede o excesso de links
  // vazar por cima do logo em resoluções médias.
  const isStaff = user && (user.role === "admin" || user.role === "consultor" || user.role === "clinica");
  const publicNav = isStaff ? (
    <div className="relative hidden lg:block">
      <button
        type="button"
        onClick={() => { setExploreOpen((v) => !v); setJoinOpen(false); }}
        onBlur={() => setTimeout(() => setExploreOpen(false), 150)}
        data-testid="nav-explore-toggle"
        className="text-sm text-slate-700 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
      >
        Explorar <ChevronDown className={`w-3.5 h-3.5 transition-transform ${exploreOpen ? "rotate-180" : ""}`} />
      </button>
      {exploreOpen && (
        <div className="absolute top-full right-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40" data-testid="nav-explore-menu">
          {EXPLORE_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testId} onMouseDown={(e) => e.preventDefault()} onClick={() => setExploreOpen(false)}
              className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#0B2B62] transition-colors">
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  ) : (
    <>
      <Link
        to="/pedir-ajuda"
        onClick={closeMobile}
        data-testid="nav-need-help"
        className="text-sm font-bold text-red-600 hover:text-red-700 px-3 py-2 rounded-md hover:bg-red-50 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
        Preciso de ajuda
      </Link>
      {/* Dropdown Explorar */}
      <div className="relative hidden lg:block">
        <button
          type="button"
          onClick={() => { setExploreOpen((v) => !v); setJoinOpen(false); }}
          onBlur={() => setTimeout(() => setExploreOpen(false), 150)}
          data-testid="nav-explore-toggle"
          className="text-sm text-slate-700 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50 transition-colors inline-flex items-center gap-1"
        >
          Explorar <ChevronDown className={`w-3.5 h-3.5 transition-transform ${exploreOpen ? "rotate-180" : ""}`} />
        </button>
        {exploreOpen && (
          <div className="absolute top-full right-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40" data-testid="nav-explore-menu">
            {EXPLORE_LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={l.testId} onMouseDown={(e) => e.preventDefault()} onClick={() => setExploreOpen(false)}
                className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#0B2B62] transition-colors">
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </div>
      {/* Dropdown Faça parte */}
      <div className="relative hidden lg:block">
        <button
          type="button"
          onClick={() => { setJoinOpen((v) => !v); setExploreOpen(false); }}
          onBlur={() => setTimeout(() => setJoinOpen(false), 150)}
          data-testid="nav-join-toggle"
          className="text-sm text-slate-700 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50 transition-colors inline-flex items-center gap-1"
        >
          Faça parte <ChevronDown className={`w-3.5 h-3.5 transition-transform ${joinOpen ? "rotate-180" : ""}`} />
        </button>
        {joinOpen && (
          <div className="absolute top-full right-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40" data-testid="nav-join-menu">
            {JOIN_LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={l.testId} onMouseDown={(e) => e.preventDefault()} onClick={() => setJoinOpen(false)}
                className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#0B2B62] transition-colors">
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </div>
      {/* No mobile: lista plana */}
      <div className="lg:hidden flex flex-col gap-1 pt-1 border-t border-slate-100 mt-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold px-1 pt-2">Explorar</div>
        {EXPLORE_LINKS.map((l) => (
          <Link key={l.to} to={l.to} data-testid={`${l.testId}-mobile`} onClick={closeMobile}
            className="text-sm text-slate-700 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50">
            {l.label}
          </Link>
        ))}
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold px-1 pt-2">Faça parte</div>
        {JOIN_LINKS.map((l) => (
          <Link key={l.to} to={l.to} data-testid={`${l.testId}-mobile`} onClick={closeMobile}
            className="text-sm text-slate-700 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50">
            {l.label}
          </Link>
        ))}
      </div>
    </>
  );

  const navLinks = user ? (
    <>
      {(user.role === "consultor" || user.role === "admin") && (
        <>
          <PlantaoToggle />
          <Link to="/consultor" data-testid="nav-consultor" onClick={closeMobile} className="text-sm text-slate-700 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50 transition-colors whitespace-nowrap">
            Painel
          </Link>
          <OpsDropdown role={user.role} closeMobile={closeMobile} />
        </>
      )}
      {user.role === "admin" && (
        <>
          <Link to="/admin" data-testid="nav-admin" onClick={closeMobile}
            className="text-sm font-semibold text-[#0B2B62] hover:text-slate-900 px-3 py-2 rounded-md hover:bg-slate-50 transition-colors whitespace-nowrap">
            Governança
          </Link>
          <Link to="/admin/filho-presente" data-testid="nav-admin-fp" onClick={closeMobile} className="text-xs text-amber-700 hover:text-amber-800 px-2 py-2 rounded-md hover:bg-amber-50 transition-colors whitespace-nowrap inline-flex items-center gap-1"
            title="Admin Filho Presente">
            <HeartHandshake className="w-3.5 h-3.5" strokeWidth={1.8} />
            <span className="hidden xl:inline">Admin FP</span>
          </Link>
        </>
      )}
      {user.role === "clinica" && (
        <Link to="/clinica" data-testid="nav-clinica" onClick={closeMobile} className="text-sm text-slate-700 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50 transition-colors whitespace-nowrap">
          Painel Unidade
        </Link>
      )}
      {user.role === "profissional" && (
        <Link to="/profissional" data-testid="nav-profissional" onClick={closeMobile} className="text-sm text-slate-700 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50 transition-colors whitespace-nowrap">
          Meu perfil profissional
        </Link>
      )}
      {user.role === "familia" && (
        <Link to="/meus-atendimentos" data-testid="nav-my-cases" onClick={closeMobile} className="text-sm text-slate-700 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50 transition-colors whitespace-nowrap">
          Meus atendimentos
        </Link>
      )}
      <Link to="/seguranca" data-testid="nav-security" onClick={closeMobile} className="text-xs text-slate-500 hover:text-[#0B2B62] px-2 py-2 rounded-md hover:bg-slate-50 transition-colors whitespace-nowrap">
        Segurança
      </Link>
      <Link
        to="/perfil"
        data-testid="nav-profile"
        onClick={closeMobile}
        className="text-xs text-slate-500 hover:text-[#0B2B62] px-2 py-2 rounded-md hover:bg-slate-50 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap">
        <UserCircle2 className="w-4 h-4 text-slate-500" strokeWidth={1.8} />
        <span>Meu perfil</span>
      </Link>
      {user.role === "clinica" && (
        <Link to="/clinica/perfil" data-testid="nav-clinic-profile" onClick={closeMobile} className="text-xs text-slate-500 hover:text-[#0B2B62] px-2 py-2 rounded-md hover:bg-slate-50 transition-colors whitespace-nowrap">
          Unidade
        </Link>
      )}
    </>
  ) : (
    <>
      <Link to="/entrar" data-testid="nav-login" onClick={closeMobile} className="text-sm text-slate-700 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50 transition-colors">
        Entrar
      </Link>
      <Link to="/cadastro" data-testid="nav-register" onClick={closeMobile} className="text-sm bg-[#0B2B62] text-white px-4 py-2 rounded-md hover:bg-slate-800 transition-colors">
        Criar conta
      </Link>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur-sm fixed top-0 left-0 right-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 shrink-0">
            <img src="/raf-logo.webp" alt="RAF Direct" className="h-10 w-auto" width="60" height="40" decoding="async" fetchpriority="high" />
            <div className="leading-tight hidden sm:block border-l border-slate-200 pl-2.5 ml-1">
              <div className="font-display font-bold text-[#0B2B62] text-sm tracking-tight">Emergência</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">Acolhimento · Direcionamento</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-end min-w-0">
            {!isStaff && (
              <Link
                to={user?.role === "familia" ? "/filho-presente/painel" : "/filho-presente"}
                onClick={closeMobile}
                data-testid="nav-filho-presente"
                className="text-xs font-semibold text-amber-700 hover:text-amber-800 px-2.5 py-2 rounded-md hover:bg-amber-50 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                title="Filho Presente — Cuidados para idosos"
              >
                <HeartHandshake className="w-4 h-4" strokeWidth={1.8} />
                <span className="hidden xl:inline">Filho Presente</span>
                <span className="xl:hidden">FP</span>
              </Link>
            )}
            {publicNav}
            <span className="w-px h-6 bg-slate-200 mx-1" />
            {navLinks}
            {user && (
              <div className="flex items-center gap-2 pl-3 border-l border-slate-200 ml-1 shrink-0">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                ) : (
                  <UserCircle2 className="w-5 h-5 text-slate-500 shrink-0" strokeWidth={1.5} />
                )}
                <span
                  className="text-sm text-slate-700 truncate max-w-[110px] text-right"
                  title={user.name}
                  data-testid="user-name"
                >
                  {shortName(user.name)}
                </span>
                <button
                  data-testid="logout-btn"
                  onClick={() => { logout(); nav("/"); }}
                  className="text-slate-500 hover:text-red-600 p-1.5 rounded-md hover:bg-slate-50 transition-colors shrink-0"
                  title="Sair"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.8} />
                </button>
              </div>
            )}
          </nav>

          {/* Mobile trigger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white shadow-lg" data-testid="mobile-nav-drawer">
            <div className="px-4 py-3 flex flex-col gap-1 max-h-[75vh] overflow-y-auto">
              {!isStaff && (
                <Link to={user?.role === "familia" ? "/filho-presente/painel" : "/filho-presente"} onClick={closeMobile} data-testid="nav-filho-presente-mobile"
                  className="text-sm font-semibold text-amber-700 hover:text-amber-800 px-3 py-2 rounded-md hover:bg-amber-50 inline-flex items-center gap-1.5">
                  <HeartHandshake className="w-4 h-4" strokeWidth={1.8} />
                  Filho Presente
                </Link>
              )}
              {publicNav}
              {navLinks}
              {user && (
                <div className="pt-3 mt-2 border-t border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCircle2 className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
                    <span className="text-sm text-slate-700" data-testid="user-name-mobile">{user.name}</span>
                  </div>
                  <button
                    data-testid="logout-btn-mobile"
                    onClick={() => { logout(); closeMobile(); nav("/"); }}
                    className="text-sm text-red-600 inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" strokeWidth={1.8} />
                    Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      {/* Espaçador para compensar o header fixed */}
      <div className="h-16" aria-hidden="true" />
      {/* Botão global Voltar — some na home e páginas de auth */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-3">
        <BackButton />
      </div>
      <main>{children}</main>
      <OnboardingTour />
      <footer className="border-t border-slate-200 mt-16 bg-white" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 md:grid-cols-12 gap-8 text-left">
          {/* Marca — coluna larga; alinha à esquerda em qualquer largura */}
          <div className="md:col-span-4 flex flex-col items-start">
            <div className="flex items-center gap-3">
              <img src="/raf-logo.webp" alt="RAF Direct" className="h-11 w-auto" width="66" height="44" decoding="async" loading="lazy" />
              <div className="font-display font-bold text-xl text-[#0B2B62] tracking-tight">RAF Direct</div>
            </div>
            <p className="text-sm text-slate-500 mt-4 leading-relaxed max-w-sm">
              Suporte especializado e orientação acolhedora para famílias que buscam ajuda na reabilitação.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">Faça parte</div>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/filho-presente/cadastre-clinica" data-testid="footer-fp-apply-clinic"
                  className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 font-semibold">
                  🏥 Cadastre sua clínica ou ILPI
                </Link>
              </li>
              <li>
                <Link to="/filho-presente/cadastre-profissional" data-testid="footer-fp-apply-pro"
                  className="text-slate-600 hover:text-[#0B2B62]">
                  Cadastro Filho Presente — profissional
                </Link>
              </li>
              <li>
                <Link to="/seja-profissional" data-testid="footer-raf-apply-pro"
                  className="text-slate-600 hover:text-[#0B2B62]">
                  Cadastro RAF Direct — profissional
                </Link>
              </li>
              <li>
                <Link to="/seja-parceiro" data-testid="footer-raf-apply-clinic"
                  className="text-slate-600 hover:text-[#0B2B62]">
                  Cadastro RAF Direct — clínica
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">Suporte & institucional</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/atendimento" data-testid="footer-atendimento" className="text-slate-600 hover:text-[#0B2B62]">Atendimento e orientação</Link></li>
              <li><Link to="/faq" data-testid="footer-faq" className="text-slate-600 hover:text-[#0B2B62]">Perguntas frequentes</Link></li>
              <li><Link to="/compliance" data-testid="footer-compliance" className="text-slate-600 hover:text-[#0B2B62]">Compliance · Dados institucionais</Link></li>
              <li><Link to="/politica-de-privacidade" data-testid="footer-privacy" className="text-slate-600 hover:text-[#0B2B62]">Política de Privacidade</Link></li>
              <li><Link to="/termos-de-uso" data-testid="footer-terms" className="text-slate-600 hover:text-[#0B2B62]">Termos de Uso</Link></li>
              <li><Link to="/termo-de-confidencialidade" data-testid="footer-confidentiality" className="text-slate-600 hover:text-[#0B2B62]">Termo de Confidencialidade</Link></li>
              <li><Link to="/politica-de-cookies" data-testid="footer-cookies-policy" className="text-slate-600 hover:text-[#0B2B62]">Política de Cookies</Link></li>
              <li><Link to="/meus-dados" data-testid="footer-lgpd-rights" className="text-slate-600 hover:text-[#0B2B62]">Meus dados · Direitos LGPD</Link></li>
              <li>
                <button
                  type="button"
                  onClick={() => window.rafConsent && window.rafConsent.open && window.rafConsent.open()}
                  data-testid="footer-cookie-prefs"
                  className="text-slate-600 hover:text-[#0B2B62] text-left"
                >
                  Preferências de cookies
                </button>
              </li>
              <li><Link to="/seguranca" data-testid="footer-security" className="text-slate-600 hover:text-[#0B2B62]">Segurança & privacidade</Link></li>
              <li><a href="mailto:contato@rafdirect.com.br" className="text-slate-600 hover:text-[#0B2B62] break-all">contato@rafdirect.com.br</a></li>
              <li><span className="text-slate-500">Atendimento: seg–dom, 24h</span></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">Conformidade</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>LGPD · Dados criptografados</li>
              <li>Auditoria hash-chained</li>
              <li>Certificação verificável via QR</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
            <div>© 2026 RAF Direct · Acolhimento Inteligente</div>
            <div>Do primeiro contato à admissão — em um só lugar.</div>
          </div>
        </div>
      </footer>
    </div>
  );
};
